import React, { useState, useRef, useEffect } from "react";
import SectionHeader from "../../Common/SectionHeader/SectionHeader";
import TopNavBar from "../../TopNavBar/TopNavBar";
import { Modal } from "bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addPolicyPersonalAccident } from "../../../Redux/PersonalAccidentRedux";
import {
  addPolicyTermPlan,
  getPlanDocumentLife,
  getQuestionsTermPlan,
} from "../../../Redux/TermPlanRedux";
import { useTranslation } from "react-i18next";
import "../../Questions/HealthQuestions/HealthQuestionsStyle.scss";
import { updateLeadsLife } from "../../../Redux/LogsRedux/Life/LifeLeadsRedux";
import { documentUpload } from "../../../Redux/LoadingRedux";
import UploadDocument from "../../UploadDocument/UploadDocument";
import CustomModal from "../../Common/CustomModal/CustomModal";

function HealthQuestionsTerm() {
  const navigate = useNavigate();
  const [show, setShow] = useState(true);
  const modalRef = useRef(null);
  const bsModalRef = useRef(null);
  const canvasRef = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [understandCoverage, setUnderstandCoverage] = useState(false);
  const [questions, setQuestion] = useState([]);
  const [map, setMap] = useState([]);
  const [rawQuestions, setRawQuestions] = useState(null);
  const [showUploadStep, setShowUploadStep] = useState(false);
  const [submittedPlanDetails, setSubmittedPlanDetails] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useSelector((state) => state.loading);
  const isAr = selectedLanguage === "ar" || i18n.language?.startsWith("ar");
  const location = useLocation();
  const { planDetails } = location.state || {};

  const [answers, setAnswers] = useState("");
  const [details, setDetails] = useState("");
  const [fileName, setFileName] = useState();
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);

  let allAnswered = "";
  if (answers && Array.isArray(answers)) {
    allAnswered = answers.every((ans) => ans === "yes" || ans === "no");
  }

  useEffect(() => {
    if (!rawQuestions || !Array.isArray(rawQuestions)) return;

    if (selectedLanguage === "en") {
      const questionList = rawQuestions.map((item) => item.English);
      const mapList = rawQuestions.map((item) => item.key);
      setQuestion(questionList);
      setMap(mapList);

      // initialize answers array only if not already set or length mismatch
      if (!Array.isArray(answers) || answers.length !== questionList.length) {
        if (planDetails?.healthQuestion) {
          const initialAnswers = mapList.map(key => (planDetails.healthQuestion[key] || "").toLowerCase());
          setAnswers(initialAnswers);
          setDetails(Array(initialAnswers.length).fill(""));
        } else {
          setAnswers(Array(questionList.length).fill(""));
          setDetails(Array(questionList.length).fill(""));
        }
      }
    } else if (selectedLanguage === "ar") {
      const questionList = rawQuestions.map((item) => item.Arabic);
      const mapList = rawQuestions.map((item) => item.key);
      setQuestion(questionList);
      setMap(mapList);

      if (!Array.isArray(answers) || answers.length !== questionList.length) {
        if (planDetails?.healthQuestion) {
          const initialAnswers = mapList.map(key => (planDetails.healthQuestion[key] || "").toLowerCase());
          setAnswers(initialAnswers);
          setDetails(Array(initialAnswers.length).fill(""));
        } else {
          setAnswers(Array(questionList.length).fill(""));
          setDetails(Array(questionList.length).fill(""));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage, rawQuestions]);

  useEffect(() => {
    if (planDetails?.healthQuestions) {
      const prefilledAnswers = Array(questions.length).fill("");
      Object.entries(planDetails.healthQuestions).forEach(([key, value]) => {
        const idx = map.indexOf(key);
        if (idx !== -1 && value) {
          // Convert "Yes"/"No" to "yes"/"no" for button comparison
          prefilledAnswers[idx] = value.toLowerCase() === "yes" ? "yes" : "no";
        }
      });
      setAnswers(prefilledAnswers);
    }
  }, [planDetails?.healthQuestions, questions, map]);

  useEffect(() => {
    if (modalRef.current) {
      bsModalRef.current = new Modal(modalRef.current, {
        backdrop: "static",
        keyboard: false,
      });
      bsModalRef.current.show();
      modalRef.current.addEventListener("hidden.bs.modal", () =>
        setShow(false),
      );
    }

    dispatch(getQuestionsTermPlan())
      .then((result) => {
        if (result.success && Array.isArray(result.data)) {
          setRawQuestions(result.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching questions:", err);
      });
  }, []);

  const handleClose = () => {
    if (bsModalRef.current) bsModalRef.current.hide();
  };

  const handleAnswer = (index, answer) => {
    const newAnswers = [...answers];
    newAnswers[index] = answer;
    setAnswers(newAnswers);
  };

  const handleDetailChange = (index, value) => {
    const newDetails = [...details];
    newDetails[index] = value;
    setDetails(newDetails);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let drawing = false;

    const startDrawing = (e) => {
      drawing = true;
      ctx.beginPath();
      ctx.moveTo(
        e.clientX - canvas.getBoundingClientRect().left,
        e.clientY - canvas.getBoundingClientRect().top,
      );
    };

    const draw = (e) => {
      if (!drawing) return;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#000";
      ctx.lineTo(
        e.clientX - canvas.getBoundingClientRect().left,
        e.clientY - canvas.getBoundingClientRect().top,
      );
      ctx.stroke();
      setIsSigned(true);
    };

    const stopDrawing = () => {
      drawing = false;
    };

    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("mouseleave", stopDrawing);

    // Touch events for mobile
    canvas.addEventListener("touchstart", (e) => {
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent("mousedown", {
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      canvas.dispatchEvent(mouseEvent);
    });

    canvas.addEventListener("touchmove", (e) => {
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent("mousemove", {
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      canvas.dispatchEvent(mouseEvent);
    });

    canvas.addEventListener("touchend", () => {
      const mouseEvent = new MouseEvent("mouseup");
      canvas.dispatchEvent(mouseEvent);
    });

    return () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("mouseleave", stopDrawing);
    };
  }, []);

  const handleClearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsSigned(false);
  };

  const canProceed =
    allAnswered && isSigned && understandCoverage && agreeTerms;

  const getSignatureDataUrl = () => {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    return canvas.toDataURL();
  };

  const handleSubmit = async () => {
    const selectedLimit = planDetails?.selectedLimit || {};
    const selectedPlanName = planDetails?.selectedPlan?.planName || "";
    const planType = selectedPlanName.split(" ")[0] || selectedPlanName;

    const annual_limit =
      selectedLimit.annualLimit != null
        ? Number(selectedLimit.annualLimit).toLocaleString()
        : "";

    const op =
      selectedLimit.opDetectable != null
        ? String(selectedLimit.opDetectable)
        : "";

    const co_pay =
      selectedLimit.coPay != null
        ? (() => {
            const raw = String(selectedLimit.coPay).trim();
            return raw.includes("%") ? raw : `${raw}%`;
          })()
        : selectedLimit.coPay || "";

    const personal =
      planDetails?.personalInformation ||
      planDetails?.basicInfo ||
      planDetails?.personalInformation?.personalInformation ||
      {};

    const answersMapped = {};
    answers.forEach((ans, idx) => {
      const key = map[idx];
      if (key)
        answersMapped[key] = ans === "" ? "No" : ans === "yes" ? "Yes" : "No";
    });

    const rawDataUrl = getSignatureDataUrl();
    const rawBase64 = rawDataUrl ? rawDataUrl.split(",")[1] || "" : "";

    const payload = {
      cover_type: planDetails?.personalInformation?.cover_type || "",
      application_type:
        planDetails?.personalInformation?.application_type || "",
      plan: planDetails?.personalInformation?.plan || "",
      nationality: planDetails?.personalInformation?.nationality,
      civil_id: planDetails?.personalInformation?.civil_id || "",
      sum_assured: planDetails?.selectedLimit?.sum_assured || "",
      personalInformation: {
        fullName:
          planDetails?.personalInformation?.personalInformation?.fullName || "",
        email:
          planDetails?.personalInformation?.personalInformation?.email || "",
        mobileNumber:
          planDetails?.personalInformation?.personalInformation?.mobileNumber ||
          "",
        dob: planDetails?.basicInfo?.dob || "",
        resident_location:
          planDetails?.personalInformation?.personalInformation
            ?.resident_location || "",
        passport:
          planDetails?.personalInformation?.personalInformation?.passport || "",
        gender: planDetails?.basicInfo?.gender || "",
        is_married: planDetails?.basicInfo?.married || "",
        height:
          planDetails?.personalInformation?.personalInformation?.height || "",
        weight:
          planDetails?.personalInformation?.personalInformation?.weight || "",
        occupation:
          planDetails?.personalInformation?.personalInformation?.occupation ||
          "",
        occupation_code:
          planDetails?.personalInformation?.personalInformation
            ?.occupation_code || "",
        id_expiry_date:
          planDetails?.personalInformation?.personalInformation
            ?.id_expiry_date || "",
        phone: planDetails?.personalInformation?.personalInformation?.phone,
      },
      beneficiaries: planDetails?.beneficiary,
      amount: planDetails?.quoteDetails?.amount || {},
      ...answersMapped,
      // agree_terms: agreeTerms,
      understood_coverage: understandCoverage,
      signature: canvasRef.current.toDataURL(),
      send_documents_via_whatsapp:
        planDetails?.personalInformation?.send_documents_via_whatsapp || false,
    };

    if (planDetails?.coverPlan === "Term plan") {
      dispatch(addPolicyTermPlan(payload)).then((result) => {
        if (result.success) {
          const payloadData = {
            questions: answersMapped,
            transId: localStorage.getItem("LIFE_LEAD_ID"),
            quoteNo: result.data.policy_number,
            signature: "data:image/png;base64," + rawBase64,
          };
          dispatch(updateLeadsLife(payloadData)).then((response) => {
            if (response.success) {
              localStorage.setItem("LIFE_QUOTE_ID", response.data.quoteId);
              const updatedData = {
                ...planDetails,
                policyResult: result.data,
                healthQuestion: answersMapped,
              };
              dispatch(documentUpload(false));
              setSubmittedPlanDetails(updatedData);
              // setShowUploadStep(true);
              setShowConfirm(true);
              // navigate("/summary", {
              //   state: { planDetails: updatedData },
              // });
            }
          });
        }
      });
    }
  };

  const getDownloadFileName = (policy, defaultName) => {
    if (policy === "armed-forces-questionnaire") {
      return defaultName || "Armed Forces Questionnaire for IPP.docx";
    }
    if (policy === "term-plan-certificate") {
      return defaultName || "Term Plan Certificate.docx";
    }
    return defaultName || `Policy_${policy}.pdf`;
  };

  const downloadDocumentFromResponse = async (response, policy, defaultName) => {
    const data = response?.data;
    const url =
      typeof data === "string"
        ? data
        : data?.url || data?.policy_schedule_url || data?.payment_receipt_url;

    if (url && /^(https?:)?\/\//.test(url)) {
      const link = document.createElement("a");
      link.href = url;
      link.download = getDownloadFileName(policy, defaultName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    if (!data || typeof data !== "string") {
      throw new Error("No downloadable document data received");
    }

    const extension = policy === "term-plan-certificate" ? "docx" : "pdf";
    const mimeType =
      extension === "docx"
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/pdf";

    const byteCharacters = atob(data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = getDownloadFileName(policy, defaultName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadPlanDocument = async (event, policy, fileName) => {
    if (event && event.preventDefault) event.preventDefault();
    try {
      const res = await dispatch(getPlanDocumentLife(policy));
      await downloadDocumentFromResponse(res, policy, fileName);
    } catch (err) {
      console.error("Document download failed:", err);
    }
  };

  const handleQuestionnaireDownload = async (e) => {
    e.preventDefault();

    try {
      const res = await dispatch(
        getPlanDocumentLife("armed-forces-questionnaire"),
      );

      const questionnaireUrl =
        typeof res?.data === "string" ? res.data : res?.data?.url;

      if (!questionnaireUrl) {
        throw new Error("No questionnaire link received");
      }

      const link = document.createElement("a");
      link.href = questionnaireUrl;
      link.download = "Armed Forces Questionnaire for IPP.docx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Questionnaire download failed:", err);
    }
  };

  const handleBack = () => {
    navigate("/life-insurance/personal-information", {
      state: { planDetails },
    });
  };

  return (
    <>
      <CustomModal
        isOpen={isSummaryOpen}
        onClose={() => {
          setIsSummaryOpen(false);
          if (selectedPdf) {
            URL.revokeObjectURL(selectedPdf);
            setSelectedPdf(null);
          }
        }}
        title={fileName}
        download={true}
        pdfUrl={selectedPdf}
      >
        <div
          className="modal-body"
          style={{ overflow: "hidden", padding: 0, height: "70vh" }}
        >
          {selectedPdf && (
            <iframe
              title="View Document"
              src={`${selectedPdf}#page=1&toolbar=0`}
              width="100%"
              height="95%"
              style={{ border: "none" }}
            />
          )}
        </div>
      </CustomModal>
      <div className="row  d-flex pb-2">
        {/* Left Side */}
        <div className="col-md-12 ">
          <h3 className="title pt-4 pb-3">
            {t("Please answer the following")}
          </h3>
          {questions.map((question, index) => {
            const isLast = index === questions.length - 1;
            const yesLabel = isLast ? "Yes" : "I Agree";
            const noLabel = isLast ? "No" : "I Disagree";

            return (
              <div key={index} className="mb-4 question-item">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h6 className="mb-0 question-text">{question}</h6>
                  </div>
                  <div className="col-md-4 d-flex justify-content-md-end pt-2 pt-md-0">
                    <div className="d-flex gap-3">
                      <button
                        type="button"
                        className={`btn btn-secondary shadow-sm px-5 py-2 ${
                          answers[index] === "yes" ? "active" : ""
                        } `}
                        onClick={() => handleAnswer(index, "yes")}
                        aria-pressed={answers[index] === "yes"}
                      >
                        {t(yesLabel)}
                      </button>

                      <button
                        type="button"
                        className={`btn btn-secondary shadow-sm px-5 py-2 ${
                          answers[index] === "no" ? "active" : ""
                        } btn shadow-sm px-5 py-2`}
                        onClick={() => handleAnswer(index, "no")}
                        aria-pressed={answers[index] === "no"}
                      >
                        {t(noLabel)}
                      </button>
                    </div>
                  </div>
                </div>

                {answers[index] === "no" && index !== questions.length - 1 && (
                  <div className="mt-3">
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder={t("Please provide more details")}
                      value={details[index]}
                      onChange={(e) =>
                        handleDetailChange(index, e.target.value)
                      }
                    ></textarea>
                  </div>
                )}
                {answers[index] === "yes" && index == questions.length - 1 && (
                  <div className="mt-4">
                    <h6 className="mb-0 question-text">
                      {t("Please download this")}{" "}
                      <a
                        className="link-color"
                        href={`${process.env.REACT_APP_API_URL}/api/v1/life/document/download/armed-forces-questionnaire`}
                        download="Armed Forces Questionnaire for IPP.docx"
                        onClick={handleQuestionnaireDownload}
                      >
                        {t("questionnaire")}
                      </a>{" "}
                      {t("and fill it")}.{/* <br /> */}
                      {t(
                        " You will be asked to upload the filled document after the payment",
                      )}
                      .
                    </h6>
                  </div>
                )}
              </div>
            );
          })}

          {/* SIGNATURE SECTION */}
          <div className="mt-5">
            <h5 className="fw-bold mb-3">
              {t("Please put your signature below")}:
            </h5>
            <div className="p-3" style={{ width: "100%", maxWidth: "400px" }}>
              <canvas
                ref={canvasRef}
                width="350"
                height="150"
                className="shadow-sm"
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                }}
              ></canvas>
              <button
                className="btn btn-primary px-4 py-2 mt-2"
                type="button"
                onClick={handleClearSignature}
              >
                {t("Clear")}
              </button>
            </div>
          </div>

          {/* TERMS SECTION */}
          <div className="mt-3">
             <div
              className={`form-check mb-3 ${isAr ? "form-check-reverse text-end" : ""}`}
            >
              <input
                className="form-check-input"
                type="checkbox"
                id="understandCoverage"
                checked={understandCoverage}
                onChange={(e) => setUnderstandCoverage(e.target.checked)}
              />
              <label className="form-check-label">
                {t("Term Life Declaration")}
              </label>
            </div>
            <div
              className={`form-check mb-3 ${isAr ? "form-check-reverse text-end" : ""}`}
            >
              <input
                className="form-check-input"
                type="checkbox"
                id="agreeTerms"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
              />
              <label className="form-check-label">
                {t("I agree to the following")}{" "}
                <button
                  type="button"
                  className="btn btn-link p-0 link-color"
                  onClick={(e) =>
                    handleDownloadPlanDocument(
                      e,
                      "term-plan-certificate",
                      "Term Plan Certificate.docx",
                      "Term Plan Certificate.docx",
                    )
                  }
                >
                  {t("Terms and Conditions")}
                </button>
              </label>
            </div>
          </div>

          {/* Proceed Button */}
          <div className="row mt-4">
            <div
              className={
                i18n.language === "ar" ? "col-6 text-end" : "col-6 text-start"
              }
            >
              <button
                type="button"
                className="btn btn-primary py-2 px-4"
                onClick={handleBack}
              >
                {t("Back")}
              </button>
            </div>
            {!showUploadStep && (
              <div
                className={
                  i18n.language === "ar" ? "col-6 text-start" : "col-6 text-end"
                }
              >
                <button
                  type="button"
                  className="btn btn-primary py-2 px-4"
                  onClick={() => handleSubmit()}
                  disabled={!canProceed}
                >
                  {t("Proceed")}
                </button>
              </div>
            )}
            {!showUploadStep && !canProceed && (
              <div className="col-12 text-danger small mt-2">
                {!allAnswered && (
                  <div>
                    {t("Please answer all questions before proceeding")}.
                  </div>
                )}
                {!isSigned && (
                  <div>
                    {t("Please provide your signature in the box above")}.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {showConfirm && (
          <>
            <div
              className="modal fade show"
              style={{ display: "block" }}
              role="dialog"
              aria-modal="true"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  {/* <div className="modal-header border-0">
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => setShowConfirm(false)}
                      ></button>
                    </div> */}
                  <div className="modal-body pt-0 pb-0">
                    <UploadDocument planDetails={submittedPlanDetails} />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}
      </div>
    </>
  );
}

export default HealthQuestionsTerm;
